import { isKnownSection } from "@/features/feed/contract"
import type { FeedResponseSection, FeedSection, FormFactor } from "@/features/feed/contract"
import { itemAt } from "@/lib/helpers/array"

// Sections whose component paints its own full-bleed band — the renderer gives
// them no flow gap (their padding is the boundary) and no page-column cap.
export const sectionIsFullBleed = (section: FeedResponseSection): boolean => {
  // The taxonomy banner is the Klarna register: one photograph edge to edge,
  // touching the chrome, with no page-column cap.
  if (section.type === "CATEGORY_COVER") return true
  // Nothing else bleeds. The reference is a grey field carrying inset rounded
  // cards, so a section that wants emphasis becomes a card (see SectionFrame),
  // and the hero is a card like everything else.
  return false
}

export const showsOn = (section: FeedResponseSection, formFactor: FormFactor) => section.presentation.formFactors.includes(formFactor)

// A section can join a row only if it shows on both form factors (a hidden member
// would leave an empty grid track) and isn't full-bleed (its background is its own
// boundary). Ineligible sections render standalone; the renderer logs the demotion.
const rowIdFor = (section: FeedSection): string | null => {
  const row = section.presentation.row
  if (!row) return null
  if (sectionIsFullBleed(section) || !showsOn(section, "MOBILE") || !showsOn(section, "DESKTOP")) return null
  return row.id
}

// Guards payload weights: missing, zero, negative, or non-finite all mean an
// equal share — a raw 0 would collapse the member to an invisible 0fr track.
export const rowWeightFor = (section: FeedSection): number => {
  const weight = section.presentation.row?.weight
  return weight != null && Number.isFinite(weight) && weight > 0 ? weight : 1
}

export interface UnitMember {
  section: FeedSection
  // Position within the flat payload — analytics keep counting per section.
  index: number
}

interface KnownMember {
  section: FeedSection
  // Index within the UNFILTERED payload — positions must not compact when an
  // unknown section type is dropped, or they skew against the scroller's raw
  // startIndex (FeedPageView passes feed.sections.length).
  payloadIndex: number
}

export const knownMembers = (sections: FeedResponseSection[]): KnownMember[] =>
  sections.flatMap((section, payloadIndex) => (isKnownSection(section) ? [{ section, payloadIndex }] : []))

// A unit is what the flow gap separates: a row of side-by-side members, or a
// single standalone/full-bleed section. Rows only form from consecutive
// eligible members sharing row.id within one batch.
export const segmentUnits = (sections: FeedSection[]): UnitMember[][] => {
  const units: UnitMember[][] = []
  let previousRowId: string | null = null
  sections.forEach((section, index) => {
    const rowId = rowIdFor(section)
    if (rowId && rowId === previousRowId) {
      units.at(-1)?.push({ section, index })
      return
    }
    units.push([{ section, index }])
    previousRowId = rowId
  })
  return units
}

export const unitShowsOn = (unit: UnitMember[], formFactor: FormFactor) => unit.some((member) => showsOn(member.section, formFactor))

export const unitIsFullBleed = (unit: UnitMember[]) => unit.length === 1 && sectionIsFullBleed(itemAt(unit, 0).section)

// Why a section carrying `row` rendered standalone anyway — for authoring logs.
export const rowAnomalyFor = (section: FeedSection, unit: UnitMember[]): "ineligible" | "unpaired" | null => {
  if (!section.presentation.row) return null
  if (!rowIdFor(section)) return "ineligible"
  return unit.length === 1 ? "unpaired" : null
}
